/**
 * @Author: Li, Yu Jia
 * @Date: 2020-03-21 10:20:49
 * @Last Modified by: Li, Yu Jia
 * @Last Modified time: 2021-06-23 15:54:58
 */

import React, { useEffect, useRef } from 'react';
import {
  hideWaitingCover,
  setBeforeUnloadListen,
  showWaitingCover,
  tokenExpirationPopupToggle,
  tokenWarningPopupToggle,
} from './../../actions';

import PropTypes from 'prop-types';
import TokenExpiration from './../TokenExpiration';
import TokenWarning from './../TokenWarning';
import { getRefreshedToken } from './../../apis';
import { tokenActionAsync } from './../../logics';
import { useDispatch } from 'react-redux';

const TokenTimeout = ({ autoRefresh = false }) => {
  let _mount = true;

  const canRefreshTokenRef = useRef(false);
  const clickingTimer = useRef(null);
  const warningTimer = useRef(null);
  const expirationTimer = useRef(null);
  const dispatch = useDispatch();
  const dispatchAction = (action) => _mount && dispatch(action);
  const warningCancelClickHandler = () => {
    dispatchAction(tokenWarningPopupToggle(false));
  };

  const warningContinueClickHandler = () => {
    clearTimeout(warningTimer.current);
    clearTimeout(expirationTimer.current);
    dispatchAction(showWaitingCover());
    getRefreshedToken()
      .then((response) => {
        const token = response.headers['apl_token'];
        const tokenTimeout = response.headers['apl_token_timeout'];
        sessionStorage.setItem('apl_token', token);
        sessionStorage.setItem('apl_token_timeout', tokenTimeout);
        setTokenWatchingTimeout();
        dispatchAction(hideWaitingCover());
        dispatchAction(tokenWarningPopupToggle(false));
      })
      .catch((_) => {
        setTokenWatchingTimeout();
        dispatchAction(hideWaitingCover());
        dispatchAction(tokenWarningPopupToggle(false));
      });
  };

  const expirationOkClickHandler = () => {
    dispatchAction(tokenExpirationPopupToggle(false));
    dispatchAction(showWaitingCover());
    window.history.back(-1);
  };

  const setTokenWatchingTimeout = () => {
    clearTimeout(warningTimer.current);
    clearTimeout(expirationTimer.current);
    if (sessionStorage.getItem('apl_token_timeout') !== null) {
      const tokenTimeout = parseInt(sessionStorage.getItem('apl_token_timeout'));
      const _warning_timer_tick = (tokenTimeout - 5) * 60 * 1000;
      const _expiration_timer_tick = tokenTimeout * 60 * 1000;
      const _warning_timer = setTimeout(() => {
        if (autoRefresh) {
          getRefreshedToken()
            .then((response) => {
              const token = response.headers['apl_token'];
              const tokenTimeout = response.headers['apl_token_timeout'];
              console.log('Auto Refresh Token:');
              console.log('Token: ' + token);
              console.log('Token Timeout: ' + tokenTimeout);
              sessionStorage.setItem('apl_token', token);
              sessionStorage.setItem('apl_token_timeout', tokenTimeout);
              setTokenWatchingTimeout();
            })
            .catch((_) => {
              setTokenWatchingTimeout();
            });
        } else {
          dispatchAction(tokenActionAsync(tokenWarningPopupToggle(true)));
          clearTimeout(warningTimer);
        }
      }, _warning_timer_tick);
      //delay 5 minutes to show Expiration popup if don't click Continue button in Warning popup
      const _expiration_timer = autoRefresh
        ? null
        : setTimeout(() => {
            dispatch(setBeforeUnloadListen(false));
            dispatchAction(tokenActionAsync(tokenWarningPopupToggle(false)));
            dispatchAction(tokenActionAsync(tokenExpirationPopupToggle(true)));
            clearTimeout(warningTimer.current);
            clearTimeout(expirationTimer.current);
          }, _expiration_timer_tick);
      warningTimer.current = _warning_timer;
      expirationTimer.current = _expiration_timer;
    }
  };

  const apl_token = sessionStorage.getItem('apl_token');

  const startClickingTimer = () => {
    clickingTimer.current = setTimeout(() => {
      canRefreshTokenRef.current = true;
    }, 5 * 60 * 1000);
  };

  useEffect(() => {
    const refreshToken = () => {
      if (canRefreshTokenRef.current) {
        canRefreshTokenRef.current = false;
        warningContinueClickHandler();
        startClickingTimer();
      }
    };
    if (!autoRefresh) {
      startClickingTimer();
      window.addEventListener('click', refreshToken);
    }

    return () => {
      clearTimeout(clickingTimer.current);
      window.removeEventListener('click', refreshToken);
    };
  }, [autoRefresh]);

  useEffect(() => {
    setTokenWatchingTimeout();
  }, [apl_token]);

  return autoRefresh ? (
    <></>
  ) : (
    <>
      <TokenWarning onCancelClick={warningCancelClickHandler} onContinueClick={warningContinueClickHandler} />
      <TokenExpiration onOkClick={expirationOkClickHandler} />
    </>
  );
};

TokenTimeout.propTypes = {
  autoRefresh: PropTypes.bool,
};

export default TokenTimeout;
